import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import React from "react";
import { TypeUtils } from "abweb/utils/TypeUtils";
import * as AbHooks from "abreact/utils/AbHooks";
import { AbIterable } from "abweb/collection/AbIterable";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AB_ITEM_SEPARATOR } from "abweb/collection/AbItemSeparator";
import { AB_ITEM_SPACER } from "abweb/collection/AbItemSpacer";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AbItemUtils } from "abreact/misc/AbItemUtils";
import { focusable } from "tabbable";
import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { Orientation } from "abreact/incubator/ui/SharedTypes";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { AbDimensions } from "abweb/utils/geometry/AbDimensions";
import { useNoTabStops, useStateWithRef } from "abreact/utils/AbHooks";
import { ArrayUtils } from "abweb/utils/ArrayUtils";

// Symbol to represent the place where the overflow button should be rendered.
export const AB_COLLAPSE_OVERFLOW_BUTTON = Symbol("AbCollapseOverflowButton");

// TODO write confluence

const calcItemKey = (item, visibleItemIndex, keyFunction) => {
  switch (item) {
    case AB_ITEM_SEPARATOR:
      return `ab-separator-${visibleItemIndex}`;
    case AB_ITEM_SPACER:
      return `ab-spacer-${visibleItemIndex}`;
    case AB_COLLAPSE_OVERFLOW_BUTTON:
      return "ab-overflow";
    default:
      return `item-${TypeUtils.itemToKey(AbEnriched.getItem(item), keyFunction)}`;
  }
};

/**
 * Generic core component for rendering a bar of items that collapse into an overflow button when the bar is shrunk. 
 * Uses priority to decide which elements should disappear first. Defaults to collapsing elements from right to left.
 */
export const AbCollapseBar = props => {
  const [measuredState, setMeasuredState, measuredStateRef] = useStateWithRef(undefined);
  const invalidateMeasuredState = () => setMeasuredState(undefined);
  if (props.items.filter(item => item === AB_COLLAPSE_OVERFLOW_BUTTON).length !== 1) throw new Error("Must have exactly one toolbar overflow button location");
  const rawAllCollapseItems = preprocessItems(props.items);
  const key = ArrayUtils.safeJoin(rawAllCollapseItems.map((item, index) => calcItemKey(item, index, props.keyFunction)));
  if (measuredState && measuredState.key !== key) {
    invalidateMeasuredState();
  }
  const allCollapseItems = rawAllCollapseItems.map((maybeEnrichedItem, index) => {
    if (maybeEnrichedItem === AB_ITEM_SEPARATOR) return AB_ITEM_SEPARATOR;
    if (maybeEnrichedItem === AB_COLLAPSE_OVERFLOW_BUTTON) return AB_COLLAPSE_OVERFLOW_BUTTON;
    if (maybeEnrichedItem === AB_ITEM_SPACER) return AB_ITEM_SPACER;
    const {
      item,
      enrichment
    } = AbEnriched.extract(maybeEnrichedItem);
    return AbEnriched.create(item, {
      ...enrichment,
      minWidth: measuredStateRef.current?.items[index].size,
      index: index
    });
  });
  const visibleItems = measuredStateRef.current ? preprocessItems(allCollapseItems.filter((_, i) => measuredStateRef.current?.indicesThatFit.has(i))) : allCollapseItems;
  const rawOverflowItems = measuredStateRef.current ? preprocessItems(allCollapseItems.filter((item, i) => item !== AB_COLLAPSE_OVERFLOW_BUTTON && !measuredStateRef.current?.indicesThatFit.has(i))) : allCollapseItems;
  const hidingOverflowButton = !visibleItems.includes(AB_COLLAPSE_OVERFLOW_BUTTON);
  const spacerAtStart = startsWithSpacer(props.items, hidingOverflowButton);
  const spacerAtEnd = startsWithSpacer(props.items.toReversed(), hidingOverflowButton);
  const overflowItems = filterOutOverflowButton(rawOverflowItems).map(AbEnriched.getItem);
  const isScrolling = element => {
    if (!measuredStateRef.current || !rootRef.current) return false;
    return element.scrollWidth > element.offsetWidth;
  };
  const resizeCallback = (_, element) => {
    if (!rootRef.current || !measuredStateRef.current) return;
    if (isScrolling(element) || isScrolling(rootRef.current)) {
      invalidateMeasuredState();
    } else {
      const dimensions = AbDimensions.fromElementContentBox(rootRef.current);
      const computedStyle = getComputedStyle(rootRef.current);
      const gap = parseInt(computedStyle.gap);
      setMeasuredState({
        ...measuredStateRef.current,
        indicesThatFit: calcIndicesThatFit(dimensions.width, gap, measuredStateRef.current.items)
      });
    }
  };
  const measureGroups = () => {
    if (!rootRef.current) return;
    const itemElements = AbIterable.fromNodeList(rootRef.current.querySelectorAll(".ab-collapse-bar > .ab-collapse-item")).toArray();
    const dimensions = AbDimensions.fromElementContentBox(rootRef.current);
    const computedStyle = getComputedStyle(rootRef.current);
    const gap = parseInt(computedStyle.gap);
    const calcItem = (barItem, element) => {
      if (barItem === AB_ITEM_SPACER) {
        return {
          size: 0,
          type: AB_ITEM_SPACER
        };
      }
      if (barItem === AB_ITEM_SEPARATOR) {
        return {
          size: 1,
          type: AB_ITEM_SEPARATOR
        };
      }
      if (barItem === AB_COLLAPSE_OVERFLOW_BUTTON) {
        return {
          size: element.clientWidth,
          type: AB_COLLAPSE_OVERFLOW_BUTTON
        };
      }
      element.style.width = "0px";
      const width = element.scrollWidth;
      element.style.width = "";
      return {
        size: width,
        priority: AbEnriched.getEnrichment(barItem)?.priority
      };
    };
    const items = itemElements.map(el => {
      const index = Number.parseInt(el.getAttribute("bar-index") ?? "NaN");
      const barItem = visibleItems[index];
      return calcItem(barItem, el);
    });
    setMeasuredState({
      key: key,
      items: items,
      indicesThatFit: calcIndicesThatFit(dimensions.width, gap, items),
      minWidth: calcCollapseBarMinWidth(gap, items)
    });
  };
  React.useLayoutEffect(() => {
    if (measuredStateRef.current) return;
    measureGroups();
  });
  const rootRef = AbHooks.useResizeObserver(resizeCallback, true);
  const noTabStopsRef = useNoTabStops();
  const focusProps = useToolbarFocus();
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ReactUtils.mergeRefs(rootRef, props.abRef, props.isToolbar ? noTabStopsRef : null)
  }, ReactUtils.componentProps("ab-collapse-bar", props, [measuredState === undefined && "ab-collapse-bar-measuring"]), {
    style: {
      ...props.style,
      minWidth: `calc(${measuredStateRef.current?.minWidth ?? 0}px + 2 * var(--ab-size-default-padding))`
    }
  }, props.isToolbar ? focusProps : {}, {
    role: props.isToolbar ? "toolbar" : undefined
  }), measuredStateRef.current && spacerAtStart && /*#__PURE__*/React.createElement(CollapseBarSpacer, {
    index: -1,
    key: calcItemKey(AB_ITEM_SPACER, -2, props.keyFunction)
  }) /* -2 to avoid key clash */, visibleItems.map((maybeEnrichedItem, visibleItemIndex) => {
    const key = calcItemKey(maybeEnrichedItem, visibleItemIndex, props.keyFunction);
    if (maybeEnrichedItem === AB_ITEM_SPACER) return /*#__PURE__*/React.createElement(CollapseBarSpacer, {
      index: visibleItemIndex,
      key: key
    });
    if (maybeEnrichedItem === AB_ITEM_SEPARATOR) return /*#__PURE__*/React.createElement(CollapseBarSeparator, {
      index: visibleItemIndex,
      key: key
    });
    return /*#__PURE__*/React.createElement(CollapseBarItem, {
      key: key,
      renderCollapsedButton: props.renderCollapsedButton,
      itemRenderer: props.itemRenderer,
      visibleItemIndex: visibleItemIndex,
      resizeCallback: resizeCallback,
      maybeEnrichedItem: maybeEnrichedItem,
      overflowItems: overflowItems
    });
  }), measuredStateRef.current && spacerAtEnd && /*#__PURE__*/React.createElement(CollapseBarSpacer, {
    index: -1,
    key: calcItemKey(AB_ITEM_SPACER, -1, props.keyFunction)
  }) /* -1 to avoid key clash */);
};
const CollapseBarItem = props => {
  const {
    visibleItemIndex,
    maybeEnrichedItem
  } = props;
  const divRef = AbHooks.useResizeObserver(props.resizeCallback, true);
  if (maybeEnrichedItem === AB_COLLAPSE_OVERFLOW_BUTTON) return /*#__PURE__*/React.createElement("div", {
    className: "ab-collapse-item ab-collapse-overflow-button",
    "bar-index": visibleItemIndex,
    ref: divRef
  }, props.renderCollapsedButton(props.overflowItems));
  const {
    item,
    enrichment
  } = AbEnriched.extract(maybeEnrichedItem);
  const node = (props.itemRenderer ?? AbItemUtils.defaultItemDisplayName)(item);
  return /*#__PURE__*/React.createElement("div", {
    className: CssUtils.composeClassNames("ab-collapse-item", enrichment?.className),
    "bar-index": visibleItemIndex,
    style: {
      minWidth: enrichment?.minWidth
    },
    ref: divRef
  }, node);
};
const CollapseBarSeparator = props => /*#__PURE__*/React.createElement("div", {
  className: "ab-collapse-bar-separator ab-collapse-item",
  "bar-index": props.index
});
// -1 index indicates spacers on the far left or far right that aren't part of size calculations
const CollapseBarSpacer = props => /*#__PURE__*/React.createElement("div", {
  className: CssUtils.composeClassNames("ab-collapse-bar-spacer", "ab-collapse-item"),
  "bar-index": props.index === -1 ? undefined : props.index
});
export const calcCollapseBarMinWidth = (gapSize, items) => {
  return calcIndicesThatFitHelper(-1, gapSize, items.map((item, index) => ({
    ...item,
    priority: item.type === AB_COLLAPSE_OVERFLOW_BUTTON ? Number.POSITIVE_INFINITY : item.priority ?? 0,
    index: index
  })), false).spaceUsed;
};
export const calcIndicesThatFit = (totalSpace, gapSize, items) => {
  return calcIndicesThatFitHelper(totalSpace, gapSize, items.map((item, index) => ({
    ...item,
    priority: item.type === AB_COLLAPSE_OVERFLOW_BUTTON ? Number.POSITIVE_INFINITY : item.priority ?? 0,
    index: index
  })), false).indicesThatFit;
};
const getMaxPriorityOfGroup = (type, items, itemIndex, increment) => {
  let currMaxPriority = Number.NEGATIVE_INFINITY;
  let currIndex = itemIndex + increment;
  while (true) {
    if (currIndex < 0 || currIndex >= items.length) break;
    const item = items[currIndex];
    if (item.type === AB_ITEM_SPACER) break; // Separators or spacers look for group surrounded by spacers 
    if (item.type === AB_ITEM_SEPARATOR && type === AB_ITEM_SEPARATOR) break; // Only separators look for group surrounded by separators
    if (item.type !== AB_ITEM_SEPARATOR) currMaxPriority = Math.max(currMaxPriority, item.priority);
    currIndex += increment;
  }
  return currMaxPriority;
};
const calcIndicesThatFitHelper = (totalSpace, gapSize, items, needPopupButton) => {
  if (!needPopupButton && items.some(item => item.priority === Number.NEGATIVE_INFINITY)) {
    return calcIndicesThatFitHelper(totalSpace, gapSize, items, true);
  }
  const itemsWithPriorities = items.filter(item => needPopupButton || item.type !== AB_COLLAPSE_OVERFLOW_BUTTON).filter(item => item.priority > Number.NEGATIVE_INFINITY).map((item, index, filteredItems) => {
    if (item.type === AB_ITEM_SEPARATOR || item.type === AB_ITEM_SPACER) {
      const prevPriority = getMaxPriorityOfGroup(item.type, filteredItems, index, -1);
      const nextPriority = getMaxPriorityOfGroup(item.type, filteredItems, index, 1);
      return {
        ...item,
        priority: (prevPriority + nextPriority) / 2
      }; // Separators get average priority of their neighbors
    }
    return item;
  });
  itemsWithPriorities.sort((item1, item2) => {
    if (item1.priority === item2.priority) return item1.index - item2.index;
    return item2.priority - item1.priority;
  });
  const indicesThatFit = new Set();
  let spaceUsed = 0;
  for (let i = 0; i < itemsWithPriorities.length; i++) {
    const newSpaceNeeded = itemsWithPriorities[i].size + (i > 0 ? gapSize : 0);
    if (spaceUsed + newSpaceNeeded > totalSpace && itemsWithPriorities[i].priority < Number.POSITIVE_INFINITY) {
      if (needPopupButton) break;else {
        return calcIndicesThatFitHelper(totalSpace, gapSize, items, true);
      }
    } else {
      spaceUsed += newSpaceNeeded;
      indicesThatFit.add(itemsWithPriorities[i].index);
    }
  }
  return {
    spaceUsed: spaceUsed,
    indicesThatFit: indicesThatFit
  };
};
const filterOutOverflowButton = items => {
  return items.filter(item => item !== AB_COLLAPSE_OVERFLOW_BUTTON);
};
export const startsWithSpacer = (items, skipOverflowButton) => {
  for (const item of items) {
    switch (item) {
      case false:
      case undefined:
      case null:
      case AB_ITEM_SEPARATOR:
        continue;
      case AB_ITEM_SPACER:
        return true;
      case AB_COLLAPSE_OVERFLOW_BUTTON:
        if (skipOverflowButton) continue;else return false;
      default:
        return false;
    }
  }
  return false;
};
const UNSET = Symbol("UNSET");
export const preprocessItems = items => {
  const newItems = [];
  let prev = UNSET;
  for (let item of items) {
    if (item === false || item === undefined || item === null) continue;
    switch (prev) {
      case UNSET:
        switch (item) {
          case AB_ITEM_SEPARATOR:
            continue;
          // Separator can't be first element
          case AB_ITEM_SPACER:
            continue;
          // Spacer can't be first element
          case AB_COLLAPSE_OVERFLOW_BUTTON:
          default:
            prev = item; // Log as new prev item
            continue;
        }
      case AB_ITEM_SEPARATOR:
        switch (item) {
          case AB_ITEM_SEPARATOR:
            continue;
          // Can't have double separator
          case AB_ITEM_SPACER:
            prev = item; // Spacer is more important than separator. Can't have separator by spacer.
            continue;
          case AB_COLLAPSE_OVERFLOW_BUTTON:
          default:
            newItems.push(prev);
            prev = item;
            continue;
        }
      case AB_ITEM_SPACER:
        switch (item) {
          case AB_ITEM_SEPARATOR:
            continue;
          // Can't have separator after spacer
          case AB_ITEM_SPACER:
            continue;
          // Can't have double spacer
          case AB_COLLAPSE_OVERFLOW_BUTTON:
          default:
            newItems.push(prev);
            prev = item;
            continue;
        }
      case AB_COLLAPSE_OVERFLOW_BUTTON:
      default:
        newItems.push(prev);
        prev = item;
        continue;
    }
  }
  switch (prev) {
    case UNSET:
    case AB_ITEM_SEPARATOR:
    case AB_ITEM_SPACER:
      break;
    case AB_COLLAPSE_OVERFLOW_BUTTON:
    default:
      newItems.push(prev);
      break;
  }
  return newItems;
};
const useToolbarFocus = () => {
  const [focusWithin, setFocusWithin] = React.useState(false);
  const focusElementRef = React.useRef(undefined);
  const tabIndex = focusWithin ? undefined : 0;
  const onFocus = e => {
    // When toolbar is tabbed to, find appropriate element to focus and do it. Then take toolbar out of tab order.
    if (e.currentTarget.contains(e.target) && e.currentTarget !== e.target && focusWithin === false) setFocusWithin(true);
    if (e.target !== e.currentTarget) return;
    const focusableElements = focusable(e.currentTarget);
    if (focusableElements.length === 0) return; // Focus left on toolbar element
    setFocusWithin(true); // Can take toolbar out of tab order, has inner elements to focus
    if (focusElementRef.current && focusableElements.includes(focusElementRef.current)) {
      focusElementRef.current.focus();
      return;
    }
    focusElementRef.current = focusableElements[0];
    focusableElements[0].focus();
  };
  const onBlur = e => {
    // When focus leaves the toolbar, put the toolbar back into the tab order.
    // If I already contain the focus, skip this
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    // React bubbles events through the react DOM, but we only want tooltips to be connected to children in the HTML DOM.
    if (!e.currentTarget.contains(e.target)) return;
    setFocusWithin(false);
  };
  const onKeyDown = e => {
    KeyboardUtilsReact.arrowGroupKeyDown(e, Orientation.HORIZONTAL, element => focusElementRef.current = element, true);
  };
  return {
    tabIndex,
    onFocus,
    onBlur,
    onKeyDown
  };
};
//# sourceMappingURL=AbCollapseBar.js.map
